import type { ComponentType } from 'react';

/**
 * Manifiesto de un módulo. Cada módulo vive en src/modules/<id>/ y solo puede
 * importar de core/, ui/, shared/ y de los paquetes. Nunca de otro módulo
 * (lo comprueba scripts/check-module-boundaries.mjs en CI).
 */
export interface ModuleManifest {
  id: string;
  title: string;
  /** Ruta de primer nivel (sin barra), p. ej. "biblioteca". */
  path?: string;
  /** Pestaña dentro de /proyectos/:projectId (p. ej. "guion"). */
  projectTab?: { segment: string; label: string; order: number };
  /** Diseño que envuelve las pestañas de proyecto (solo el módulo de proyectos). */
  projectLayout?: () => Promise<{ default: ComponentType }>;
  nav?: { group: 'Trabajo' | 'Canal' | 'Sistema'; label: string; icon: string; order: number };
  load: () => Promise<{ default: ComponentType }>;
}

export interface ModuleFile {
  manifests: ModuleManifest[];
}

/** Módulos desactivados por configuración (VITE_DISABLED_MODULES="results,rights"). */
export function disabledModules(): Set<string> {
  const raw = (import.meta.env.VITE_DISABLED_MODULES as string | undefined) ?? '';
  return new Set(raw.split(',').map((s) => s.trim()).filter(Boolean));
}

/**
 * Descubre los módulos presentes en src/modules/*\/index.ts. Si una carpeta de
 * módulo se elimina, simplemente deja de aparecer; los demás siguen funcionando.
 */
export function loadRegistry(): ModuleManifest[] {
  const files = import.meta.glob<ModuleFile>('../modules/*/index.ts', { eager: true });
  const off = disabledModules();
  const all: ModuleManifest[] = [];
  for (const [path, file] of Object.entries(files)) {
    if (!file || !Array.isArray(file.manifests)) {
      console.error(`Módulo inválido ignorado: ${path}`);
      continue;
    }
    for (const m of file.manifests) if (!off.has(m.id)) all.push(m);
  }
  return all;
}
